import { useCallback } from "react";
import type { User } from "@supabase/supabase-js";
import * as orderService from "@/supabase/order/service/orderService";
import { logError } from "@/lib/sentry";
import { toast } from "sonner";
import { Item } from "@/types/purchase";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";

/** 편집 중인 아이템과 (이름, 가격)이 일치하는 다른 고객 주문 수 — 주문 단위 카운트. */
export interface BulkApplyTargets {
  /** 상태 '미지정'이라 일괄 적용 가능한 주문 수 (체크박스 노출 조건 = > 0) */
  unassigned: number;
  /** 부분 입금/입금 완료/출고 완료라 제외되는 주문 수 (라벨 제외 안내용) */
  progressed: number;
}

interface UseSameProductBulkEditProps {
  user: User | null;
  activeCustomerId: string | null;
  customerTemplates: CustomerTemplate[];
  currentItems: Item[];
  /** usePurchaseState.updateItem — 낙관갱신+롤백+alert를 자체 수행하고 실패 시 throw하는 단일 수정 프리미티브 */
  updateItemInOrder: (index: number, data: Partial<Omit<Item, "id">>) => Promise<void>;
  refreshOrders: () => Promise<void>;
  /** Home 소유 단일 인스턴스 주입 (훅 자체 생성 금지 규율) */
  extractOrderId: (customerId: string) => number | null;
}

interface UseSameProductBulkEditResult {
  getBulkApplyTargets: (item: Item) => BulkApplyTargets;
  handleUpdateItemWithSameProducts: (index: number, changes: Partial<Item>) => Promise<void>;
}

/**
 * 동일 상품 일괄 수정 도메인 훅.
 * 아이템 편집 저장 시 "같은 날짜의 다른 '미지정' 주문"에서 수정 전 (이름, 가격)이 일치하는
 * 상품에 이름/가격 변경을 함께 적용한다 (ProductList의 opt-in 체크박스에서만 발동).
 *
 * 불변식:
 * - 전파 대상 = 상태 '미지정' 주문만. 진행된 주문(부분 입금/입금 완료/출고 완료)의 가격을 바꾸면
 *   입금액≠주문액 불일치가 생기므로 이름만 변경이어도 항상 제외한다(오너 확정).
 *   강제는 서버 RPC(bulk_update_same_product_items)가 행 잠금(FOR UPDATE) 하에 같은 날짜/미지정/
 *   원본 (이름,가격) 매칭을 원자적으로 수행 — 클라 카운트는 표시용일 뿐 진실이 아니다.
 * - 전파 스코프 날짜 = 활성 주문의 order_date를 서버가 도출(클라이언트는 날짜를 보내지 않음).
 * - 수량은 고객별 값이라 절대 전파하지 않는다(RPC에 파라미터 자체가 없음).
 * - 활성 고객 단일 수정은 기존 프리미티브(updateItemInOrder)를 그대로 사용 — 실패 시 전파 중단.
 * - 다른 고객 낙관적 미러링 금지 — 전파 후 refreshOrders()로 DB 진실 재동기화가 유일한 반영 경로.
 * - 전파된 타 고객의 parsed_order 상품명은 조용히 stale해질 수 있음(배송정보 nudge는 활성 고객만
 *   감시하므로 미발화) — v1 수용 한계, 백로그 = 대상 주문 scheduleCheck 연동.
 */
export function useSameProductBulkEdit({
  user,
  activeCustomerId,
  customerTemplates,
  currentItems,
  updateItemInOrder,
  refreshOrders,
  extractOrderId,
}: UseSameProductBulkEditProps): UseSameProductBulkEditResult {
  const getBulkApplyTargets = useCallback((item: Item): BulkApplyTargets => {
    // v1은 로그인 + DB 주문(order_*)만 — 비로그인/temp 고객은 체크박스 자체가 안 뜬다.
    if (!user || !activeCustomerId || !activeCustomerId.startsWith("order_")) {
      return { unassigned: 0, progressed: 0 };
    }
    let unassigned = 0;
    let progressed = 0;
    for (const customer of customerTemplates) {
      if (customer.id === activeCustomerId || !customer.id.startsWith("order_")) continue;
      const hasMatch = (customer.items ?? []).some(
        other => other.name === item.name && other.price === item.price
      );
      if (!hasMatch) continue;
      if (customer.status === "미지정") unassigned += 1;
      else progressed += 1;
    }
    return { unassigned, progressed };
  }, [user, activeCustomerId, customerTemplates]);

  const handleUpdateItemWithSameProducts = useCallback(async (index: number, changes: Partial<Item>) => {
    // 수정 "전" 원본/스코프 스냅샷 — await 이전 캡처(이름 변경 시에도 원본 이름+가격으로 매칭 보장,
    // 저장 도중 고객 전환이 일어나도 진입 시점 주문 기준 유지)
    const original = currentItems[index];
    if (!original) return;
    const customerId = activeCustomerId;
    const activeOrderId = customerId ? extractOrderId(customerId) : null;
    // 제외 안내 카운트(표시용): 진행된 주문 중 원본 (이름, 가격) 매칭 주문 수
    let progressedMatchCount = 0;
    for (const customer of customerTemplates) {
      if (customer.id === customerId || !customer.id.startsWith("order_")) continue;
      if (customer.status === "미지정") continue;
      if ((customer.items ?? []).some(other => other.name === original.name && other.price === original.price)) {
        progressedMatchCount += 1;
      }
    }

    try {
      await updateItemInOrder(index, changes);
    } catch {
      // 롤백+알림은 updateItemInOrder가 이미 수행 — 전파만 중단
      return;
    }

    const propagated: { itemName?: string; price?: number } = {};
    if (changes.name !== undefined) propagated.itemName = changes.name;
    if (changes.price !== undefined) propagated.price = changes.price;
    if (Object.keys(propagated).length === 0) {
      toast.info("수량 변경은 함께 적용되지 않아 이 주문에만 저장했습니다.", { duration: 5000 });
      return;
    }

    if (activeOrderId === null) {
      // 체크박스 노출 조건상 도달 불가 방어선 — 단일 수정은 이미 저장됨
      logError("동일 상품 일괄 적용: 활성 주문 스코프 식별 실패", { customerId });
      toast.error("다른 주문에 적용하지 못했습니다. 이 주문의 수정은 저장되었습니다.", { duration: 6000 });
      return;
    }

    const fieldLabel =
      propagated.itemName !== undefined && propagated.price !== undefined
        ? "상품명·가격"
        : propagated.itemName !== undefined
          ? "상품명"
          : "가격";

    try {
      const { appliedOrderCount } = await orderService.bulkUpdateSameProductItems({
        excludeOrderId: activeOrderId,
        match: { itemName: original.name, price: original.price },
        changes: propagated,
      });

      if (appliedOrderCount === 0) {
        toast.info("함께 적용할 '미지정' 주문이 없어 이 주문만 수정했습니다.");
      } else {
        toast.success(
          `같은 날짜 '미지정' 주문 ${appliedOrderCount}건에 ${fieldLabel}을 함께 적용했습니다.` +
            (progressedMatchCount > 0 ? ` (입금·출고된 ${progressedMatchCount}건 제외)` : "")
        );
      }
    } catch (error) {
      logError("동일 상품 일괄 적용 중 오류 발생:", error);
      toast.error("다른 주문에 적용하지 못했습니다. 이 주문의 수정은 저장되었습니다.", { duration: 6000 });
    } finally {
      // 성공/0건/실패 전부 DB 진실로 재동기화 (미러 state 금지 규율)
      try {
        await refreshOrders();
      } catch (refreshError) {
        logError("동일 상품 일괄 적용 후 주문 재동기화 실패:", refreshError);
      }
    }
  }, [currentItems, updateItemInOrder, customerTemplates, activeCustomerId, extractOrderId, refreshOrders]);

  return { getBulkApplyTargets, handleUpdateItemWithSameProducts };
}
